import { FC, useState, useEffect } from "react";
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Dropdown, Field, MessageBar, MessageBarBody, Option, Spinner, Text } from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { getUserGroupsByModuleAndOrganizationID } from "../../../services/userGroupService";
import { User } from "../../../models/users";
import { useAppContext } from "../../../AppContext";

interface Props {
  onClose: () => void;
  open: boolean;
  onShare: (users: User[]) => void;
  existingUsers?: User[];
  userGroupID: number;
  moduleID: number;
  organizationID: number;
}

const SharePlacement: FC<Props> = ({ onClose, open, onShare, existingUsers = [], userGroupID, moduleID, organizationID }) => {
  const { user: loggedUser } = useAppContext();
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [groupUsers, setGroupUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [userError, setUserError] = useState<string>("");
  const [isSharing, setIsSharing] = useState<boolean>(false);

  // Populate chips with existing users when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedUsers(existingUsers);
      setUserError("");
    } else {
      setSelectedUsers([]);
    }
  }, [open, existingUsers]);

  // Load the members of the placement's user group to choose from
  useEffect(() => {
    if (!open) return;

    const loadGroupUsers = async () => {
      setLoadingUsers(true);
      try {
        const groups = await getUserGroupsByModuleAndOrganizationID(moduleID, organizationID);
        const group = groups.find((g: any) => g.userGroupID === userGroupID);
        // Only Kommunanvändare can own a placement; Behörighetsansvarig-only members can't be selected
        const members = (group?.userGroupAssignments ?? [])
          .filter((a: any) => a.roles?.some((r: any) => r.roleName === "Kommunanvändare"))
          .map((a: any) => a.user);
        setGroupUsers(members);
      } catch (error) {
        console.error("Error fetching user group members:", error);
        setGroupUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    loadGroupUsers();
  }, [open, moduleID, organizationID, userGroupID]);

  const availableUsers = groupUsers.filter(
    (u) => !selectedUsers.some((s) => s.userID === u.userID)
  );

  const handleRemoveUser = (userToRemove: User) => {
    // You can't remove your own access to the placement
    if (userToRemove.userID === loggedUser?.user.userID) {
      setUserError("Du kan inte ta bort dig själv från förfrågan");
      return;
    }

    // Ensure at least one user remains
    if (selectedUsers.length <= 1) {
      setUserError("Minst en användare måste finnas kvar");
      return;
    }

    setSelectedUsers(selectedUsers.filter((u) => u.userID !== userToRemove.userID));
    setUserError("");
  };

  const handleShare = async () => {
    if (selectedUsers.length === 0) {
      setUserError("Minst en användare krävs");
      return;
    }

    setIsSharing(true);
    setUserError("");

    try {
      await onShare(selectedUsers);
      onClose();
    } catch (error) {
      setUserError("Ett fel uppstod vid delning");
    } finally {
      setIsSharing(false);
    }
  };

  const handleClose = () => {
    setSelectedUsers([]);
    setUserError("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(_event, data) => !data.open && handleClose()}>
      <DialogSurface style={{ width: "600px", maxWidth: "600px", height: "600px" }}>
        <DialogBody style={{ height: "100%" }}>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                shape="circular"
                aria-label="close"
                icon={<DismissRegular />}
                onClick={handleClose}
              />
            }
          >
            Dela förfrågan
          </DialogTitle>
          <DialogContent style={{ flexGrow: 1, overflowY: "auto" }}>
            <MessageBar intent="info" className="mb-3">
              <MessageBarBody>
                Du behåller själv din tillgång till förfrågan. Välj vilka fler användare inom gruppen som också ska ha tillgång.
              </MessageBarBody>
            </MessageBar>
            <Text>
              Följande användare har tillgång till denna förfrågan. Endast dessa kan se och arbeta med innehållet:
            </Text>
            <Field
              validationMessage={userError}
              validationState={userError ? "error" : "none"}
              required
              className="mt-3"
            >
              <div className="flex flex-wrap gap-2 border border-gray-300 rounded-md p-1 min-h-[36px] max-h-[140px] overflow-y-auto">
                {selectedUsers.map((selectedUser) => {
                  const isSelf = selectedUser.userID === loggedUser?.user.userID;
                  return (
                    <div key={selectedUser.userID} className="flex items-center gap-1 px-2 py-1 rounded-full text-sm bg-blue-100 text-blue-700">
                      <span>{selectedUser.firstName} {selectedUser.lastName} ({selectedUser.email}){isSelf ? " (du)" : ""}</span>
                      {!isSelf && (
                        <Button
                          appearance="transparent"
                          size="small"
                          icon={<DismissRegular />}
                          onClick={() => handleRemoveUser(selectedUser)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </Field>
            <Field label="Lägg till användare från gruppen" className="mt-3">
              <Dropdown
                multiselect
                placeholder={loadingUsers ? "Laddar användare..." : "Välj användare att lägga till"}
                value=""
                selectedOptions={[]}
                disabled={loadingUsers}
                // DialogContent scrolls internally, which otherwise clips the popup's available height to the dialog's bounds
                positioning={{ overflowBoundary: 'window' }}
                listbox={{ style: { maxHeight: '250px', overflowY: 'auto' } }}
                onOptionSelect={(_, data) => {
                  const usersToAdd = availableUsers.filter((u) => data.selectedOptions.includes(u.userID.toString()));
                  if (usersToAdd.length > 0) {
                    setSelectedUsers((prev) => [...prev, ...usersToAdd]);
                    setUserError("");
                  }
                }}
              >
                {availableUsers.length === 0 ? (
                  <Option key="none" value="none" disabled text="Inga fler användare att välja">
                    Inga fler användare att välja
                  </Option>
                ) : (
                  availableUsers.map((u) => (
                    <Option key={u.userID} value={u.userID.toString()} text={`${u.firstName} ${u.lastName}`}>
                      {u.firstName} {u.lastName} ({u.email})
                    </Option>
                  ))
                )}
              </Dropdown>
            </Field>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={isSharing}>Avbryt</Button>
            <Button 
              appearance="primary" 
              onClick={handleShare}
              disabled={isSharing}
              icon={isSharing ? <Spinner size="tiny" /> : undefined}
            >
              {isSharing ? 'Delar...' : 'Spara'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default SharePlacement;
